import { Button, ButtonLevel, ButtonSize, TextSize } from '@vizit/atoms';
import { Trash01Icon } from '@vizit/icons';
import { Dialog, DialogHeaderStatus, DialogHeaderVariant, type DialogProps } from '@vizit/molecules';
import * as React from 'react';

interface DeleteImagesDialogProps extends Omit<DialogProps, 'children'> {
  /**
   * How many images will be deleted.
   *
   * @required
   */
  count: number;
  /**
   * Deletes the images. The dialog shows a loading Delete button until it settles; closing is up to the caller.
   *
   * @required
   */
  onConfirm: () => Promise<void>;
  title?: string;
  description?: React.ReactNode;
  confirmLabel?: string;
  /**
   * Content under the description, e.g. a notice about what else the removal affects.
   *
   * @optional
   */
  children?: React.ReactNode;
}

/**
 * Confirmation before deleting collection images: one from its tile (`count={1}`) or many from the action bar
 * (BLU-353). Both use this dialog so the copy can't drift apart (SDD D79). Deleting is permanent: images leave the
 * Asset Library, not only the collection, together with their Spark images (SDD D55), so the copy says so.
 *
 * @example
 * <DeleteImagesDialog isOpen={isOpen} onOpenChange={setIsOpen} count={3} onConfirm={deleteSelected} />
 */
function DeleteImagesDialog(props: DeleteImagesDialogProps) {
  const {
    isOpen = false,
    onOpenChange,
    count,
    onConfirm,
    title,
    description,
    confirmLabel = 'Delete',
    children,
    ...other
  } = props;
  const [isDeleting, setIsDeleting] = React.useState(false);

  const handleConfirm = React.useCallback(async () => {
    setIsDeleting(true);
    try {
      await onConfirm();
    } finally {
      setIsDeleting(false);
    }
  }, [onConfirm]);

  const isOne = count === 1;

  return (
    <Dialog isOpen={isOpen} onOpenChange={onOpenChange} {...other}>
      <Dialog.Overlay>
        <Dialog.Content dialogClassName="gap-4">
          <Dialog.Header variant={DialogHeaderVariant.Icon} status={DialogHeaderStatus.Error} icon={Trash01Icon}>
            <Dialog.Title showEllipsis={true}>
              {title ?? (isOne ? 'Delete 1 image' : `Delete ${count} images`)}
            </Dialog.Title>
          </Dialog.Header>
          <div className="flex flex-col gap-8 px-12">
            <Dialog.Subtitle elementType="p" size={TextSize.Md} className="text-secondary" showEllipsis={false}>
              {description ?? (
                <>
                  {isOne ? 'This image is' : 'These images are'} deleted from your Asset Library, not only from this
                  collection, together with the Spark images made from {isOne ? 'it' : 'them'}. This can&apos;t be
                  undone.
                </>
              )}
            </Dialog.Subtitle>
            {children}
          </div>
          <Dialog.Footer className="grid grid-cols-2 gap-6">
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              level={ButtonLevel.Secondary}
              onPress={() => onOpenChange?.(false)}
              isDisabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              size={ButtonSize.Lg}
              type="submit"
              level={ButtonLevel.Danger}
              onPress={handleConfirm}
              isLoading={isDeleting}
            >
              {confirmLabel}
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Overlay>
    </Dialog>
  );
}

export { DeleteImagesDialog };
export type { DeleteImagesDialogProps };
